import {
  CompensationMethodType,
  DraftReturn,
} from "@redotech/redo-model/draft-return/draft-return";
import { ReturnableItem } from "@redotech/redo-model/draft-return/draft-return-items";
import { compensationMethodTypeMap } from "@redotech/redo-model/draft-return/util";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { returnOptionJsonFormat } from "@redotech/redo-model/return-flow";
export function isExchangeAgentBonusEligible(
  draftReturn: DraftReturn,
): boolean {
  if (
    draftReturn.merchantCreated ||
    draftReturn.returnType !== ReturnTypeEnum.RETURN
  ) {
    return false;
  }

  return draftReturn.pendingReturnItems.some(
    (item) => item.selectedReturnOption?.shopNowBonusEligible,
  );
}

export function exchangeAgentHasCompensationAdjustment(
  draftReturn: DraftReturn,
): boolean {
  const bonusEligibleReturnOption = draftReturn.pendingReturnItems.find(
    (item) => !!item.selectedReturnOption?.shopNowBonusEligible,
  )?.selectedReturnOption;
  if (!bonusEligibleReturnOption) {
    return false;
  }

  const methodType = returnOptionJsonFormat.read(bonusEligibleReturnOption)
    .method.type;
  const returnMethod = draftReturn.compensationMethods.find(
    (method) => compensationMethodTypeMap[method.type] === methodType,
  );
  if (!returnMethod) {
    return false;
  }

  const exchangeMethod = draftReturn.compensationMethods.find(
    (method) =>
      method.type === CompensationMethodType.EXCHANGE &&
      method.advancedExchangeAvailable,
  );
  if (!exchangeMethod) {
    return false;
  }

  return (
    returnMethod.totalReturnableValue.toNumber() <
    exchangeMethod.totalReturnableValue.toNumber()
  );
}

export function getExchangeAgentExchangeBonusForSingleItem(
  draftReturn: DraftReturn,
  returnableItem: ReturnableItem,
): number {
  const exchangeMethod = draftReturn.compensationMethods.find(
    (method) =>
      method.type === CompensationMethodType.EXCHANGE &&
      method.advancedExchangeAvailable,
  );
  if (!exchangeMethod) {
    return 0;
  }

  const onlyOneReturnableItem =
    draftReturn.returnableItems.filter((item) => item.selected).length === 1;
  if (!onlyOneReturnableItem) {
    return 0;
  }

  if (
    draftReturn.pendingReturnItems.length !== 1 ||
    !draftReturn.pendingReturnItems[0]?.metadata?.temporary ||
    draftReturn.pendingReturnItems[0]?.returnableItemId !== returnableItem.id
  ) {
    return 0;
  }

  const allNonExchangeMethods = draftReturn.compensationMethods.filter(
    (method) =>
      method.type !== CompensationMethodType.EXCHANGE &&
      method.type !== CompensationMethodType.REPAIR,
  );

  let bonus = 0;
  for (const method of allNonExchangeMethods) {
    const difference = Math.max(
      exchangeMethod.totalReturnableValue
        .minus(method.totalReturnableValue)
        .toNumber(),
      0,
    );
    if (difference >= 0) {
      bonus = Math.max(bonus, difference);
    }
  }

  return bonus;
}
